import React, { Component, createRef } from "react";
import { Button } from "react-bootstrap";
import logo from "../../logo.svg";
import "./css/NavMain.scss";
import navitems from "../../navitems.json";
import AuthContext from "../../context/AuthContext";
import { withRouter } from "../../utils/withRouter";
import { motion, AnimatePresence } from "framer-motion";
import jwtDecode from "jwt-decode";
import axios from "axios";
import GLOBALVAR from "../../constants";
import debounce from 'lodash/debounce';

class NavMain extends Component {
    static contextType = AuthContext;

    constructor(props) {
        super(props);
        this.state = {
            activeItem: null,
            isOpen: false,
            searchQuery: "",
            filteredItems: [],
            applicantResults: [],
            userMenuOpen: false,
            isSearching: false
        };
        this.navRef = createRef();
        this.searchRef = createRef();
        this.resizeObservers = [];
        
        // Create debounced search function
        this.debouncedSearch = debounce(this.performSearch, 300);
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        document.addEventListener("click", this.handleOutsideClick);
        // Add a small delay to ensure DOM is ready
        setTimeout(() => {
            this.setupResizeObservers();
        }, 100);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener("click", this.handleOutsideClick);
        // Safely disconnect all observers
        if (this.resizeObservers && this.resizeObservers.length > 0) {
            this.resizeObservers.forEach(observer => {
                if (observer && typeof observer.disconnect === 'function') {
                    observer.disconnect();
                }
            });
        }
        // Cancel any pending debounced calls
        if (this.debouncedSearch) {
            this.debouncedSearch.cancel();
        }
    }

    setupResizeObservers = () => {
        // Clear existing observers safely
        if (this.resizeObservers && this.resizeObservers.length > 0) {
            this.resizeObservers.forEach(observer => {
                if (observer && typeof observer.disconnect === 'function') {
                    observer.disconnect();
                }
            });
        }
        this.resizeObservers = [];

        // Add new observers with a small delay
        setTimeout(() => {
            const children = document.querySelectorAll(".navigation__children");
            if (children && children.length > 0) {
                children.forEach((child) => {
                    if (child) {
                        const observer = new ResizeObserver(entries => {
                            requestAnimationFrame(() => {
                                for (let entry of entries) {
                                    const height = entry.contentRect.height;
                                    const parent = entry.target.closest(".navigation__children");

                                    if (parent && height > 400 && window.matchMedia("(max-width: 768px)").matches) {
                                        parent.style.marginTop = `-${height / 2}px`;
                                    }
                                }
                            });
                        });
                        observer.observe(child);
                        this.resizeObservers.push(observer);
                    }
                });
            }
        }, 50);
    };

    handleClick = (index) => {
        // Use requestAnimationFrame for smoother state updates
        requestAnimationFrame(() => {
            this.setState(prevState => ({
                activeItem: prevState.activeItem === index ? null : index
            }), () => {
                // Delay the resize observer setup slightly
                setTimeout(() => {
                    this.setupResizeObservers();
                }, 50);
            });
        });
    };

    handleToggle = () => {
        // Add a small delay to ensure smooth animation
        requestAnimationFrame(() => {
            this.setState(prev => ({
                isOpen: !prev.isOpen
            }));
            document.body.classList.toggle('nav-open');
        });
    };

    handleOutsideClick = (event) => {
        if (!event.target.closest("#navigation")) {
            document.querySelectorAll(".navigation__parent").forEach((el) => {
                el.classList.remove("active-temp");
            });
        }
    };

    handleLogout = () => {
        const { logout } = this.context; // Get logout function from AuthContext
        logout(); // Perform logout
        this.props.navigate("/login"); // Redirect to login
    };

    handleSearch = (e) => {
        const query = e.target.value;
        this.setState({ 
            searchQuery: query,
            isSearching: true
        });

        // Immediately filter menu items
        const matchedParentIds = new Set();
        const filtered = navitems.filter(item => {
            const matchInMain = item.name.toLowerCase().includes(query.toLowerCase());
            const matchInSub = item.subitem?.some(sub => {
                const matches = sub.name.toLowerCase().includes(query.toLowerCase());
                if (matches) {
                    matchedParentIds.add(item.id);
                }
                return matches;
            });
            return matchInMain || matchInSub;
        });

        this.setState({ 
            filteredItems: filtered,
            activeItem: matchedParentIds.size === 1 ? Array.from(matchedParentIds)[0] : null
        });

        // Debounce the API call
        this.debouncedSearch(query);
    };

    performSearch = async (query) => {
        if (query.length < 2) {
            this.setState({ 
                applicantResults: [],
                isSearching: false
            });
            return;
        }

        try {
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/applicationsearch`, {
                params: {
                    query: query,
                    page: 1,
                    limit: 10
                },
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.data && response.data.rows) {
                const applicantResults = response.data.rows.map(applicant => ({
                    id: `applicant-${applicant.id}`,
                    name: `${applicant.lastName}, ${applicant.firstName} ${applicant.middleName}`,
                    type: 'applicant',
                    slug: `applicant/${applicant.id}`,
                    icon: 'fas fa-file-alt',
                    subitem: []
                }));

                this.setState({ 
                    applicantResults: applicantResults,
                    isSearching: false
                });
            }
        } catch (error) {
            console.error('Error searching applications:', error);
            this.setState({ 
                applicantResults: [],
                isSearching: false
            });
        }
    };

    // Update the highlightMatch method to handle applicant results
    highlightMatch = (text, query) => {
        if (!query) return text;
        
        const parts = text.split(new RegExp(`(${query})`, 'gi'));
        return parts.map((part, index) => 
            part.toLowerCase() === query.toLowerCase() ? 
                <span key={index} className="highlight">{part}</span> : part
        );
    };

    handleResize = () => {
        this.setState({ isOpen: window.innerWidth > 768 });
    };

    toggleUserMenu = () => {
        this.setState(prev => ({ userMenuOpen: !prev.userMenuOpen }));
    };

    render() {
        const { isOpen, activeItem, searchQuery, filteredItems, applicantResults, userMenuOpen } = this.state;
        const { slug } = this.props;
        const itemsToRender = searchQuery ? filteredItems : navitems;
        const user = {
            name: localStorage.getItem("name"),
            role: jwtDecode(localStorage.getItem("token")).permissions
        };

        return (
            <>
                {/* Mobile Header */}
                <header className="main-header d-md-none">
                    <a href="/" className="header-logo">
                        <img src={logo} alt="Hayahay Corp Logo" className="nav-logo" />
                    </a>
                    <button 
                        className="mobile-toggle"
                        onClick={this.handleToggle}
                        aria-label="Toggle Navigation"
                    >
                        <i className="fas fa-bars" />
                        <span>Menu</span>
                    </button>
                </header>

                {/* Overlay */}
                <div 
                    className={`nav-overlay ${isOpen ? 'visible' : ''}`} 
                    onClick={this.handleToggle}
                    style={{ transitionDelay: isOpen ? '0s' : '0.1s' }}
                />

                {/* Navigation */}
                <nav 
                    className={`main-navigation ${isOpen ? 'nav-open' : ''}`}
                    style={{ transitionDelay: isOpen ? '0.1s' : '0s' }}
                >
                    <div className="nav-header">
                        <div className="logo-container">
                            <a href="/" className="logo-link">
                                <img src={logo} alt="Hayahay Corp Logo" className="nav-logo" />
                            </a>
                        </div>

                        <Button 
                            className="nav-toggle"
                            onClick={this.handleToggle}
                            aria-label="Toggle Navigation"
                        >
                            <i className={`fas fa-${isOpen ? 'times' : 'bars'}`} />
                        </Button>
                    </div>

                    <div className="nav-search">
                        <div className="search-container">
                            <i className="fas fa-search search-icon" />
                            <input
                                ref={this.searchRef}
                                type="text"
                                placeholder="Search menu and applications..."
                                value={searchQuery}
                                onChange={this.handleSearch}
                                className="search-input"
                            />
                            {this.state.isSearching && (
                                <div className="search-loading">
                                    <i className="fas fa-spinner fa-spin" />
                                </div>
                            )}
                            {searchQuery && !this.state.isSearching && (
                                <button
                                    type="button"
                                    className="clear-search"
                                    onClick={() => {
                                        this.setState({ 
                                            searchQuery: '', 
                                            filteredItems: [],
                                            applicantResults: [],
                                            isSearching: false 
                                        });
                                        this.searchRef.current?.focus();
                                    }}
                                    aria-label="Clear search"
                                >
                                    <i className="fas fa-times-circle" />
                                </button>
                            )}
                        </div>
                        {searchQuery && (
                            <div className="search-info">
                                {this.state.isSearching ? (
                                    <span className="searching-text">Searching...</span>
                                ) : filteredItems.length > 0 ? (
                                    <span className="results-count">
                                        Found {filteredItems.length} result{filteredItems.length !== 1 ? 's' : ''}
                                    </span>
                                ) : (
                                    <span className="no-results">No menu found</span>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="nav-content">
                        <div className="nav-menu">
                            {/* Menu Items */}
                            {itemsToRender.filter(item => !item.type || item.type !== 'applicant').map((item, index) => (
                                <motion.div
                                    key={item.id}
                                    className={`nav-item ${activeItem === item.id ? 'active' : ''}`}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ 
                                        delay: index * 0.05,
                                        duration: 0.2,
                                        ease: [0.4, 0, 0.2, 1]
                                    }}
                                >
                                    <a 
                                        href={item.slug ? `/${item.slug}` : '#'}
                                        className={`nav-link ${item.subitem?.length > 0 ? 'has-children' : ''}`}
                                        onClick={(e) => {
                                            if (!item.slug || item.subitem?.length > 0) {
                                                e.preventDefault();
                                                this.handleClick(item.id);
                                            }
                                        }}
                                    >
                                        <i className={`${item.icon} nav-icon`} />
                                        <span className="nav-label">
                                            {this.highlightMatch(item.name, searchQuery)}
                                        </span>
                                        {item.subitem?.length > 0 && (
                                            <i className={`fas fa-chevron-${activeItem === item.id ? 'down' : 'right'} nav-arrow`} />
                                        )}
                                    </a>

                                    <AnimatePresence mode="wait">
                                        {(activeItem === item.id || 
                                         (searchQuery && item.subitem?.some(sub => 
                                            sub.name.toLowerCase().includes(searchQuery.toLowerCase())
                                         ))) && 
                                         item.subitem?.length > 0 && (
                                            <motion.div
                                                className="subnav"
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{
                                                    duration: 0.2,
                                                    ease: [0.4, 0, 0.2, 1]
                                                }}
                                            >
                                                {item.subitem.map(sub => (
                                                    <a
                                                        key={sub.slug}
                                                        href={`/${sub.slug}`}
                                                        className={`subnav-link ${
                                                            slug === sub.slug ? 'active' : ''
                                                        } ${
                                                            searchQuery && 
                                                            sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ? 
                                                            'search-match' : ''
                                                        }`}
                                                    >
                                                        {this.highlightMatch(sub.name, searchQuery)}
                                                    </a>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            ))}

                            {/* Applicant Results */}
                            {searchQuery && applicantResults.length > 0 && (
                                <>
                                    <div className="search-results-section">
                                        <span>Applicant Results</span>
                                    </div>
                                    {applicantResults.map((item, index) => (
                                        <motion.div
                                            key={item.id}
                                            className="nav-item applicant-result"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ 
                                                delay: index * 0.05,
                                                duration: 0.2,
                                                ease: [0.4, 0, 0.2, 1]
                                            }}
                                        >
                                            <a 
                                                href={`/${item.slug}`}
                                                className="nav-link"
                                            >
                                                <i className={`${item.icon} nav-icon`} />
                                                <span className="nav-label">
                                                    {this.highlightMatch(item.name, searchQuery)}
                                                </span>
                                            </a>
                                        </motion.div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="nav-footer">
                        <div className="user-menu">
                            <Button 
                                className="user-menu-toggle"
                                onClick={this.toggleUserMenu}
                            >
                                <div className="user-avatar">
                                    <i className="fas fa-user" />
                                </div>
                                <div className="user-info">
                                    <span className="user-name">{user?.name || 'User'}</span>
                                    <span className="user-role">{user?.role || 'Admin'}</span>
                                </div>
                                <i className={`fas fa-chevron-${userMenuOpen ? 'up' : 'down'}`} />
                            </Button>

                            <AnimatePresence>
                                {userMenuOpen && (
                                    <motion.div 
                                        className="user-dropdown"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                    >
                                        {/*
                                        <a href="/profile" className="dropdown-item">
                                            <i className="fas fa-user-circle" /> Profile
                                        </a>
                                        <a href="/settings" className="dropdown-item">
                                            <i className="fas fa-cog" /> Settings
                                        </a>
                                        */
                                        }
                                        <button 
                                            onClick={this.handleLogout}
                                            className="dropdown-item text-danger"
                                        >
                                            <i className="fas fa-sign-out-alt" /> Logout
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </nav>
            </>
        );
    }
}

export default withRouter(NavMain);
